/* @flow */
/*
*  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
*  This file is licensed to you under the Apache License, Version 2.0 (the "License");
*  you may not use this file except in compliance with the License. You may obtain a copy
*  of the License at http://www.apache.org/licenses/LICENSE-2.0
*
*  Unless required by applicable law or agreed to in writing, software distributed under
*  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
*  OF ANY KIND, either express or implied. See the License for the specific language
*  governing permissions and limitations under the License.
*
*/

import Ingest from '@cchome/ingest-react';
import analyticsUtil, {getComName, getSvcName, getInstallWorkflowName} from '../utils/AnalyticsUtil';
import thorUtil from '../utils/wam/ThorUtil';
import {setExpCampaignAndVariation} from '../utils/TestUtil';
import uuid from 'uuid';
import Url from '../../src/utils/Url';
import appsUtil from '../utils/AppsUtil';
import DeviceDetectionUtil from '../utils/DeviceDetectionUtil';
import {allTab, AppsToCCXUICategoryMaping} from '../constants/appsConstants';
import GMOData from './GMOMarketing';
import {ANALYTICS} from '@ccxi/ccxi-apps/src/utils/enums';

let logAnalytics = 0;
let logMetricsCollection = 0;
const constants = {
	rsidDev: 'adbem-cchome-dev',
	rsidProd: 'adbem-cchome-prod'
};

const WORKFLOWS = ['App Picker', 'Post Purchase Feedback', 'Project Guides', 'Intent-Selection', 'Profile', 'Campaign CTA', 'CC Downloads'];

export default class AppsAnalytics {
	ingest: Object;

	constructor(env: string) {
		const initObj = analyticsUtil.initAnalytics();
		if (env) {
			initObj.environment = env;
		}
		initObj.rsid = constants.rsidDev;
		if (initObj.environment === 'production' || initObj.environment === 'prod') {
			initObj.rsid = constants.rsidProd;
		}

		initObj.offline = false;

		this.ingest = new Ingest(initObj);

		logMetricsCollection = 0;
		logAnalytics = 0;

		if (window.logAppsMetrics && window.logAppsMetrics > 0) {
			logMetricsCollection = 1;
		}
		if (window.logAnalytics && window.logAnalytics > 0) {
			logAnalytics = 1;
		}
	}

	/**
	 * [initAnalytics Init Ingest]
	 * @return {[none]}
	 */
	initAnalytics() {
		this.ingest.initializeDefaultData();
	}

	/**
	 * [sendToIngest Post to ingest]
	 * @return {Promise<any>}
	 */
	sendToIngest() {
		if (typeof this.ingest.metricsCollection !== 'undefined' && this.ingest.metricsCollection.length) {
			const sendToIngestPromise = this.ingest.sendToIngest();
			sendToIngestPromise.catch((err) => {
				console.error(err);
			});
			if (logMetricsCollection) {
				const arrayLength = this.ingest.metricsCollection.length;
				console.log('metricsCollection.length = ', this.ingest.metricsCollection.length);
				for (let i = 0; i < arrayLength; i++) {
					console.log('sendToIngest metricsCollection = ', this.ingest.metricsCollection[i].events[0].data);
				}
			}
			// send the events to the CCHome intents server also
			if (window.eventsService) {
				window.eventsService.reportEvents(this.ingest.metricsCollection);
			}
			return sendToIngestPromise;
		}
		return Promise.reject({error:'No ingest metrics'});
	}

	isStarter() {
		return (Url.getParam('isStarter') || appsUtil.getPathData().mainAppsTab === 'starter');
	}

	/**
	 * [replaceSpaces replace spaces with dashes]
	 * @param  {[string]} text
	 * @return {[string]}  modified text
	 */
	replaceSpaces(text: string) {
		let newVal = text;

		newVal = newVal.replace(/\s+/g, '-');

		// if the first char is now a '-', get rid of it
		if (newVal.length && newVal.charAt(0) === '-') {
			newVal = newVal.substr(1);
		}

		return newVal;
	}


	/**
	 * [logAnalytics A utility method to set multiple values at once]
	 * @param  {[object of key/value pairs]} dataObject [events]
	 * @return {[none]}
	 */
	logAnalytics(dataObject: Object, workflow: ?string) {
		let osVersion = this.ingest.getOSVersion();
		// removing win or mac from osversion as per haresh suggestion
		if (appsUtil.isWin()) {
			osVersion += `:${DeviceDetectionUtil.is64bitWindows() ? '64' : '32'}`;
		}
		else if (appsUtil.isMacOS()) {
			osVersion += `:${DeviceDetectionUtil.is64bitMac() ? '64' : '32'}`;
		}
		WORKFLOWS.indexOf(workflow) !== -1 ? this.ingest.setDataValue('event', 'workflow', workflow) : this.ingest.setDataValue('event', 'workflow', 'Apps');
		this.ingest.setDataValue('source', 'os_version', osVersion);

		// adding workflow info in ui.sequence
		const sequenceVal = getInstallWorkflowName();
		this.ingest.setDataValue('ui', 'sequence', sequenceVal);

		if (typeof dataObject.setUIFilter === 'undefined' && this.isStarter()) {
			dataObject.setUIFilter = 'starter';
		}

		if (appsUtil.isFFCFallback) {
			dataObject.setEventContextSequence = 'ffc-fallback';
		}

		dataObject.setIngestContextGuid = analyticsUtil.contextGuid;
		dataObject.setIngestContextParams = analyticsUtil.contextParams;
		dataObject.setIngestContextInit = analyticsUtil.contextInit;

		if (!analyticsUtil.isSplitEvent(dataObject) && !analyticsUtil.isSophiaProfileSetEvent(dataObject) && !analyticsUtil.isCoreRuleFailEvent(dataObject)) {
			setExpCampaignAndVariation(dataObject);
		}
		else if (!dataObject.setExpCampaignID && appsUtil.isMobileOrTab()) {
			dataObject.setExpCampaignID = 'CCH-Apps-Mobile-201909';
			dataObject.setExpVariationID = 'test';
		}

		// Set env com name and env svc name
		dataObject.setEnvComName = getComName();
		dataObject.setEnvironmentServiceName = getSvcName();

		Object.keys(dataObject).forEach((key) => {
			const value = dataObject[key];
			if (value !== null && typeof value !== 'undefined') {
				switch (key) {
					case 'setEventSubcategory':
						this.ingest.setDataValue('event', 'subcategory', value);
						break;
					case 'setEventValue':
						this.ingest.setDataValue('event', 'value', value);
						break;
					case 'setEventType':
						this.ingest.setDataValue('event', 'type', value);
						break;
					case 'setEventSubtype':
						this.ingest.setDataValue('event', 'subtype', value);
						break;
					case 'setEventCount':
						this.ingest.setDataValue('event', 'count', value);
						break;
					case 'setEventOffline':
						this.ingest.setDataValue('event', 'offline', value);
						break;
					case 'setEventContextGUID':
						this.ingest.setDataValue('event', 'context_guid', value);
						break;
					case 'setEventGUID':
						this.ingest.setDataValue('event', 'guid', value);
						break;
					case 'setEventContextSequence':
						this.ingest.setDataValue('event', 'context_sequence', value);
						break;
					case 'setContentID':
						this.ingest.setDataValue('content', 'id', value);
						break;
					case 'setContentName': {
						// Splunk seems to be having problems with spaces
						const newVal = this.replaceSpaces(value);
						this.ingest.setDataValue('content', 'name', newVal);
					}
						break;
					case 'setContentType':
						this.ingest.setDataValue('content', 'type', value);
						break;
					case 'setContentCategory':
						this.ingest.setDataValue('content', 'category', value);
						break;
					case 'setContentAction':
						this.ingest.setDataValue('content', 'action', value);
						break;
					case 'setContentAuthor':
						this.ingest.setDataValue('content', 'author', value);
						break;
					case 'setContentStatus':
						this.ingest.setDataValue('content', 'status', value);
						break;
					case 'setUIFilter':
						this.ingest.setDataValue('ui', 'filter', value);
						break;
					case 'setUIViewType':
						this.ingest.setDataValue('ui', 'view_type', value);
						break;
					case 'setUISearchKeyword':
						this.ingest.setDataValue('ui', 'search_keyword', value);
						break;
					case 'setEnvComName':
						this.ingest.setDataValue('env', 'com.name', value);
						break;
					case 'setEnvironmentServiceName':
						this.ingest.setDataValue('env', 'svc.name', value);
						break;
					case 'setEnvironmentServiceVersion':
						this.ingest.setDataValue('env', 'svc.version', value);
						break;
					case 'setComponentVersion':
						this.ingest.setDataValue('env', 'com.version', value);
						break;
					case 'setFrameworkName':
						this.ingest.setDataValue('env', 'fw.name', value);
						break;
					case 'setErrorCode':
						this.ingest.setDataValue('event', 'error_code', value);
						break;
					case 'setErrorType':
						this.ingest.setDataValue('event', 'error_type', value);
						break;
					case 'setErrorDesc':
						this.ingest.setDataValue('event', 'error_desc', value);
						break;
					case 'setSearchKeyword':
						this.ingest.setDataValue('event', 'search_keyword', value);
						break;
					case 'setContentExtension': // Sends the latest version of the app being downloaded
						this.ingest.setDataValue('content', 'extension', value);
						break;
					case 'setExpCampaignID':
						this.ingest.setDataValue('exp', 'campaign_id', value);
						break;
					case 'setExpVariationID':
						this.ingest.setDataValue('exp', 'variation_id', value);
						break;
					case 'setExpExperienceID':
						this.ingest.setDataValue('exp', 'experience_id', value);
						break;
					case 'setIngestContextGuid':
						this.ingest.setDataValue('context', 'guid', value);
						break;
					case 'setIngestContextParams':
						this.ingest.setDataValue('context', 'params', value);
						break;
					case 'setIngestContextInit':
						this.ingest.setDataValue('context', 'init', value);
						break;
					default:
						console.log('Invalid key for Apps Service');
						break;
				}
			}
		});

		if (appsUtil.isCCHInLMS()) {
			this.ingest.setDataValue('event', 'url', appsUtil.updateAnalyticsUrlForCanvas());
		}

		this.ingest.setDataValue('event', 'pagename', appsUtil.hasFeature('temp_cch_9198') ? appsUtil.getEventPageNameField() : document.title);

		this.ingest.addToCollection();
	}

	/**
	 *
	 * @param onCCHCookieCheck
	 */
	onCCHCookieCheck(isCookieEnabled: boolean) {
		const dataObject = {
			setEventType: 'api',
			setEventSubcategory: this.getAppsInterest(),
			setEventValue: isCookieEnabled,
			setEventSubtype: 'browser-cookie-enabled',
			setEventGUID: uuid.v4(),
			setEventOffline: false,
			setUIViewType: 'App Inline'
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onCCHCookieCheck: ', dataObject);
		}

		this.sendToIngest();
	}

	/**
	 *
	 * @param isTokenValid
	 */
	onCCHTokenCheck(isTokenValid: boolean) {
		const dataObject = {
			setEventType: 'auth',
			setEventSubcategory: this.getAppsInterest(),
			setEventValue: isTokenValid,
			setEventSubtype: 'signin-check',
			setEventGUID: uuid.v4(),
			setEventOffline: false,
			setUIViewType: 'App Inline'
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onCCHTokenCheck: ', dataObject);
		}

		return this.sendToIngest();
	}

	/**
	 *
	 * @param connectedToWam
	 */
	onCCHAndWAMConnection(connectedToWam: boolean, errorType: ?string, errorDesc: ?string) {
		const dataObject = {
			setEventType: 'api',
			setEventSubcategory: this.getAppsInterest(),
			setEventValue: connectedToWam,
			setEventSubtype: 'wam',
			setEventGUID: uuid.v4(),
			setErrorType: errorType,
			setErrorDesc: errorDesc,
			setEventOffline: false,
			setUIViewType: 'App Inline'
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onCCHAndWAMConnection: ', dataObject);
		}

		this.sendToIngest();
	}

	/**
	 *
	 * @param isWamAck
	 */
	onCCHGetCurrentDevice(isWamAck: boolean) {
		const dataObject = {
			setEventType: 'api',
			setEventSubcategory: this.getAppsInterest(),
			setEventValue: isWamAck,
			setEventSubtype: 'get-current-device',
			setEventGUID: uuid.v4(),
			setEventOffline: false,
			setUIViewType: 'App Inline'
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onCCHGetCurrentDevice: ', dataObject);
		}

		this.sendToIngest();
	}

	/**
	 *
	 * @param isAppsStateReceived
	 */
	onCCHRecvDeviceId(isDeviceIdReceived: boolean, errorType: ?string, errorDesc: ?string) {
		const dataObject = {
			setEventType: 'api',
			setEventSubcategory: this.getAppsInterest(),
			setEventValue: isDeviceIdReceived,
			setEventSubtype: 'receive-device-id',
			setEventGUID: uuid.v4(),
			setErrorType: errorType,
			setErrorDesc: errorDesc,
			setEventOffline: false,
			setUIViewType: 'App Inline'
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onCCHThorAppsState: ', dataObject);
		}

		this.sendToIngest();
	}

	/**
	 *
	 * @param isAppsStateReceived
	 */
	onCCHThorAppsState(isAppsStateReceived: boolean, errorType: ?string, errorDesc: ?string) {
		const dataObject = {
			setEventType: 'api',
			setEventSubcategory: this.getAppsInterest(),
			setEventValue: isAppsStateReceived,
			setEventSubtype: 'thor-apps-state',
			setEventGUID: uuid.v4(),
			setErrorType: errorType,
			setErrorDesc: errorDesc,
			setEventOffline: false,
			setUIViewType: 'App Inline'
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onCCHThorAppsState: ', dataObject);
		}

		this.sendToIngest();
	}

	/**
	 *
	 * @param isCCHRegistered
	 * @param errorType
	 * @param errorDesc
	 */
	onCCHRegistrationWithWam(isCCHRegistered: boolean, errorType: ?string, errorDesc: ?string) {
		const dataObject = {
			setEventType: 'api',
			setEventSubcategory: this.getAppsInterest(),
			setEventValue: isCCHRegistered,
			setEventSubtype: 'cch-registration-wam',
			setEventGUID: uuid.v4(),
			setErrorType: errorType,
			setErrorDesc: errorDesc,
			setEventOffline: false,
			setUIViewType: 'App Inline'
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onCCHRegistrationWithWam: ', dataObject);
		}

		this.sendToIngest();
	}

	/**
	 *
	 * @param eventName
	 * @param subType
	 * @param appConfig
	 * @param viewType
	 */
	onCCHToThorCommandForInline(eventConfig: Object, appConfig: Object) {
		const dataObject = {
			setEventType: eventConfig.eventName,
			setEventSubcategory: this.getAppsInterest(),
			setEventValue: eventConfig.eventValue,
			setEventSubtype: eventConfig.subType,
			setEventGUID: uuid.v4(),
			setEventOffline: false,
			setUIViewType: eventConfig.viewType || 'App Inline:Install',
			setSearchKeyword: eventConfig.searchKeyword,
			setEventContextSequence: eventConfig.contextSequence,
			setContentID: appConfig.sapCode,
			setContentName: appConfig.displayName,
			setContentCategory: appConfig.platform,
			setContentStatus: appConfig.entitled ? 'Entitled' : 'NotEntitled',
			setContentExtension: appConfig.version
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onCCHAndWAMConnection: ', dataObject);
		}

		this.sendToIngest();
	}

	/**
	 *
	 * @param eventName
	 * @param subType
	 * @param appConfig
	 */
	onRenderAlertAtInlineError(eventName: string, subType: string, appConfig: Object) {
		const dataObject = {
			setEventType: eventName,
			setEventSubcategory: this.getAppsInterest(),
			setEventSubtype: subType,
			setEventGUID: uuid.v4(),
			setEventOffline: false,
			setUIViewType: 'App Inline:Install',
			setContentID: appConfig.sapCode,
			setContentName: appConfig.displayName,
			setContentCategory: appConfig.platform,
			setContentStatus: appConfig.entitled ? 'Entitled' : 'NotEntitled',
			setErrorCode: appConfig.errorCode,
			setErrorType: appConfig.errorType,
			setErrorDesc: appConfig.errorDesc,
			setContentExtension: appConfig.version
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onCCHAndWAMConnection: ', dataObject);
		}

		this.sendToIngest();
	}


	/**
	 *
	 * @param connectedToThor
	 */
	onCCHToThorDetectionForInline(connectedToThor: boolean) {
		const dataObject = {
			setEventType: 'detect',
			setEventSubcategory: this.getAppsInterest(),
			setEventValue: connectedToThor,
			setEventSubtype: 'thor',
			setEventGUID: uuid.v4(),
			setEventOffline: false,
			setUIViewType: 'App Inline',
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onCCHAndWAMConnection: ', dataObject);
		}

		this.sendToIngest();
	}

	onWAMWFSuccess(timetaken: string) {
		const dataObject = {
			setEventType: 'wamWF',
			setEventSubcategory: this.getAppsInterest(),
			setEventValue: timetaken,
			setEventSubtype: 'wam-time-taken',
			setEventGUID: uuid.v4(),
			setEventOffline: false,
			setUIViewType: 'App Inline'
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('wamWF: ', dataObject);
		}
		this.sendToIngest();
	}

	onV2ToV1(subType: string) {
		const dataObject = {
			setEventType: 'v2ToV1',
			setEventSubcategory: this.getAppsInterest(),
			setEventSubtype: subType,
			setEventGUID: uuid.v4(),
			setEventOffline: false,
			setUIViewType: 'App Inline',
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onV2ToV1: ', dataObject);
		}

		this.sendToIngest();
	}

	getAppsInterest() {
		const sectionId = appsUtil.getCCWebPathData().sectionId || allTab; // default: allTab
		return ANALYTICS.LEFT_NAV[AppsToCCXUICategoryMaping[sectionId] || allTab];
	}

	getUIFilterValue(isAutoNonStarterInstallation: boolean) {
		switch (isAutoNonStarterInstallation) {
			case true:
				return 'installation';
			case false:
				return 'starter';
			default:
				return null;
		}
	}

	/**
	 *
	 * @param {EventSubcategory,tabClick} appsEventObject
	 */
	onAppClickEvent(appConfig: Object, eventSubtype: string, contentAction?: string, eventGUID?: string) {
		const dataObject = {
			setEventSubcategory: this.getAppsInterest(),
			setEventType: 'click',
			setEventSubtype: this.replaceSpaces(eventSubtype).toLowerCase(),
			setEventGUID: eventGUID ? eventGUID : uuid.v4(),
			setUIViewType: 'App',
			setContentAction: contentAction,
			setContentID: appConfig.sapCode,
			setContentName: appConfig.displayName,
			setContentCategory: appConfig.type,
			setContentStatus: appConfig.entitled ? 'Entitled' : 'NotEntitled',
			setEventValue: (appsUtil.starterRedirectPlanCode !== null) ? 'auto' : null,
			setUIFilter: this.getUIFilterValue(appsUtil.isAutoNonStarterInstallation),
			setContentExtension: appConfig.version
		};
		appsUtil.starterRedirectPlanCode = null;
		appsUtil.isAutoNonStarterInstallation = null;
		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onAppsMainRenderEvent: ', dataObject);
		}

		this.sendToIngest();
	}

	/**
	 *
	 * @param {EventSubcategory,tabClick} appsEventObject
	 */
	onAppDetailEvent(appConfig: Object,eventType: string, eventSubtype: string, contentAction?: string, eventGUID?: string) {
		const dataObject = {
			setEventSubcategory: this.getAppsInterest(),
			setEventValue: (eventType === 'render' && typeof window.cchome.navigationOccured === 'undefined') ? 'default' : null,
			setContentType: (appConfig && appConfig.isInline) ? 'inline' : null,
			setEventType: eventType,
			setEventSubtype: this.replaceSpaces(eventSubtype).toLowerCase(),
			setEventGUID: eventGUID ? eventGUID : uuid.v4(),
			setUIViewType: 'AppDetail',
			setContentAction: contentAction,
			setContentID: appConfig.sapCode,
			setContentName: appConfig.displayName,
			setContentCategory: appConfig.platform,
			setContentStatus: appConfig.entitled ? 'Entitled' : 'NotEntitled',
			setContentExtension: appConfig.version,
			setErrorType: ((appConfig.isCompatible && appConfig.isCompatibleViaThor) || (appConfig.platform !== 'desktop')) ? null : 'incompatible'
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onAppsMainRenderEvent: ', dataObject);
		}

		this.sendToIngest();
	}

	/**
	 *tabName, this.tabClicked, 'render'
	 * @param {EventSubType}
	 */
	onAppsErrorEvent(eventSubType: string, eventWorkflow: ?string) {
		const dataObject = {
			setEventType: 'api-error',
			setEventSubtype: eventSubType,
			setEventGUID: uuid.v4(),
			setEventOffline: false,
			setUIViewType: 'App'
		};

		this.logAnalytics(dataObject, eventWorkflow);

		if (logAnalytics) {
			console.log('onAppsMainErrorEvent: ', dataObject);
		}

		this.sendToIngest();
	}


	/**
	 *
	 * @param {appConfig,uIViewType,eventSubtype}
	 */
	onAppDownloadEvent(appConfig: Object, uIViewType: string, eventSubtype?: string, workflow ?: string, envConfig?: Object, eventValue?: String) {
		const hasThor = (appsUtil.hasThor !== null && appsUtil.hasThor) || !!(thorUtil.getWAMClient().isConnected());
		const setEventContextGUID = appsUtil.getContextGuid(appConfig.sapCode,hasThor);
		const dataObject: Object = {
			setEventSubcategory: this.getAppsInterest(),
			setEventType: 'download',
			setEventSubtype: eventSubtype ? this.replaceSpaces(eventSubtype).toLowerCase() : null,
			setEventValue: eventValue,
			setEventContextGUID,
			setEventGUID: uuid.v4(),
			setUIViewType: uIViewType,
			setContentID: appConfig.sapCode,
			setContentName: appConfig.displayName,
			setContentCategory: appConfig.type,
			setContentStatus: appConfig.entitled ? 'Entitled' : 'NotEntitled',
			setUIFilter: this.isStarter() ? 'starter' : 'installation',
			setContentExtension: appConfig.version
		};

		if (envConfig) {
			dataObject.setEnvironmentServiceName = envConfig.name;
			dataObject.setEnvironmentServiceVersion = envConfig.version;
		}

		this.logAnalytics(dataObject, workflow);

		if (logAnalytics) {
			console.log('onAppDownloadEvent: ', dataObject);
		}

		this.sendToIngest();

		// send adobe analytic event
		const gmoData = new GMOData();
		gmoData.productDownload(this.replaceSpaces(appConfig.displayName), (appsUtil.hasThor !== null && appsUtil.hasThor));
	}

	/**
	 *
	 * @param {appConfig} genericPDPAnalyticEvent
	 */
	genericPDPAnalyticEvent(appConfig: Object,eventType: string,eventSubtype: string,viewType: string,expCampaignID: string,expVariationID: string) {
		const dataObject = {
			setEventSubcategory: this.getAppsInterest(),
			setContentType: (appConfig && appConfig.isInline) ? 'inline' : null,
			setEventType: eventType ? eventType : 'render',
			setEventSubtype: eventSubtype ? eventSubtype : 'app-download',
			setEventGUID: uuid.v4(),
			setUIViewType: viewType ? viewType : 'Modal',
			setContentID: appConfig && appConfig.sapCode,
			setContentName: appConfig && appConfig.displayName,
			setContentCategory: appConfig && appConfig.platform,
			setContentStatus: (appConfig && appConfig.entitled) ? 'Entitled' : 'NotEntitled',
			setContentExtension: appConfig.version,
			setExpCampaignID: expCampaignID,
			setExpVariationID: expVariationID
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('genericPDPAnalyticEvent: ', dataObject);
		}

		this.sendToIngest();
	}

	/**
	 *	function for CC Growth events
	 * @param {Object} data ingestData
	 * @param {string} workflow -  Growth workflow
	 */
	growthEvent(data: Object, workflow: string) {
		const {
			comName,
			comVersion,
			variationId
		} = data;

		let ingestWorkflow = '';

		let ingestDataObject = {};

		switch (workflow) {
			case 'throughline':
				ingestWorkflow = 'Intent-Selection';
				ingestDataObject = {
					setEventCategory: 'WEB',
					setEventSubcategory: 'Split',
					setEventType: 'split',
					setEventSubtype: 'base',
					setEventOffline: false,
					setEnvComName: comName,
					setComponentVersion: comVersion,
					setExpCampaignID: '2019-09-IntentSelection',
					setExpVariationID: variationId
				};
				break;
			case 'throughline-photoshop':
				ingestWorkflow = 'Intent-Selection';
				ingestDataObject = {
					setEventCategory: 'WEB',
					setEventSubcategory: 'Split',
					setEventType: 'split',
					setEventSubtype: 'base',
					setEventOffline: false,
					setEnvComName: comName,
					setComponentVersion: comVersion,
					setExpCampaignID: '2019-12-IntentSelection-nonTWP',
					setExpVariationID: variationId
				};
				break;
			case 'throughline-v3-en':
				ingestWorkflow = 'Intent-Selection';
				ingestDataObject = {
					setEventCategory: 'WEB',
					setEventSubcategory: 'Split',
					setEventType: 'split',
					setEventSubtype: 'base',
					setEventOffline: false,
					setEnvComName: comName,
					setComponentVersion: comVersion,
					setExpCampaignID: '2020-04-IntentSelection-PS-TWP',
					setExpVariationID: variationId
				};
				break;
			case 'throughline-v3-ja':
				ingestWorkflow = 'Intent-Selection';
				ingestDataObject = {
					setEventCategory: 'WEB',
					setEventSubcategory: 'Split',
					setEventType: 'split',
					setEventSubtype: 'base',
					setEventOffline: false,
					setEnvComName: comName,
					setComponentVersion: comVersion,
					setExpCampaignID: '2020-04-IntentSelection-PS-CCPP-JA',
					setExpVariationID: variationId
				};
				break;
		}

		this.logAnalytics(ingestDataObject, ingestWorkflow);

		if (logAnalytics) {
			console.log('genericAnalyticEvent: ', ingestDataObject);
		}

		this.sendToIngest();
	}

	/**
	 *
	 * @param {appConfig} genericAnalyticEvent
	 */
	genericAnalyticEvent(appConfig: Object,eventType: string,eventSubtype: string,viewType?: string,expCampaignID?: string,expVariationID?: string, eventValue?: ?string, eventSubCategory?: string, eventWorkFlow?: string, expExperienceID?: string) {
		let envComName = 'kaizen';
		if (appsUtil.hasThor !== null && appsUtil.hasThor) {
			envComName = 'thor';
		}
		if (eventWorkFlow === 'App Picker') {
			envComName = appConfig && appConfig.displayName;
		}

		if (eventWorkFlow === 'Project Guides') {
			envComName = appConfig && appConfig.appName;
		}

		let contentStatus;
		if (appConfig && appConfig.sapCode) {
			contentStatus = appConfig.entitled ? 'Entitled' : 'NotEntitled';
		}

		let contentAuthor;
		if (eventWorkFlow === 'App Picker') {
			contentAuthor = 'designer';
		}

		const dataObject = {
			setEventSubcategory: eventSubCategory ? eventSubCategory : this.getAppsInterest(),
			setEventType: eventType,
			setEventSubtype: eventSubtype ? this.replaceSpaces(eventSubtype).toLowerCase() : null,
			setEventGUID: uuid.v4(),
			setEventContextGUID: appConfig && appConfig.eventContextGuid,
			setContentID: appConfig && appConfig.sapCode,
			setContentName: appConfig && appConfig.displayName,
			setContentType: appConfig && appConfig.compatibilityInfo, // 'incompatible' or older version of app
			setUIFilter: appConfig && appConfig.filter, // For App Picker, takes task filter for sub-category = 'Best App'
			setContentCategory: appConfig && appConfig.type,
			setContentStatus: contentStatus,
			setEnvComName: envComName,
			setComponentVersion: appConfig && appConfig.appLatestVersion,
			setContentExtension: appConfig && appConfig.version,
			setExpCampaignID: expCampaignID,
			setExpVariationID: expVariationID,
			setExpExperienceID: expExperienceID,
			setUIViewType: viewType,
			setEventValue: eventValue,
			setContentAuthor: contentAuthor,
			setContentAction: appConfig && appConfig.extURL
		};

		this.logAnalytics(dataObject, eventWorkFlow);

		if (logAnalytics) {
			console.log('genericAnalyticEvent: ', dataObject);
		}

		this.sendToIngest();
	}

	/**
	 *
	 * @param {appConfig} onCTAClickOfOSDeprecation
	 */
	onCTAClickOfOSDeprecation(appConfig: Object, eventConfig: Object) {
		const dataObject = {
			setEventSubcategory: this.getAppsInterest(),
			setEventValue: eventConfig.eventValue, // || (eventConfig.eventType === 'render' && typeof window.cchome.navigationOccured === 'undefined') ? 'default' : null,
			setContentType: (appConfig && appConfig.isInline) ? 'inline' : null,
			setEventType: 'click',
			setEventSubtype: this.replaceSpaces(eventConfig.eventSubType).toLowerCase(),
			setEventGUID: eventConfig.eventGUID || uuid.v4(),
			setEventContextGUID: eventConfig.eventContextGUID,
			setFrameworkName: eventConfig.frameworkName,
			setComponentVersion: eventConfig.componentVersion,
			setUIViewType: eventConfig.uiViewType,
			setContentAction: eventConfig.contentAction,
			setContentID: appConfig.sapCode,
			setContentName: appConfig.displayName,
			setContentCategory: appConfig.platform,
			setContentStatus: appConfig.entitled ? 'Entitled' : 'NotEntitled',
			setErrorType: ((appConfig.isCompatible && appConfig.isCompatibleViaThor) || (appConfig.platform !== 'desktop')) ? null : 'incompatible',
			setContentExtension: appConfig.version
		};

		this.logAnalytics(dataObject);

		if (logAnalytics) {
			console.log('onCTAClickOfOSDeprecation: ', dataObject);
		}

		this.sendToIngest();
	}

	getVisitorGuid() {
		return this.ingest.getVisitorGuid();
	}
}
